import { ElementType, forwardRef, HTMLAttributes, ReactNode } from 'react';
import styles from './Card.module.css';

// THE surface container for the whole app — Figma "⚠️ Symphony DS" → Card Box
// (node 3:21939). Never hand-roll a padded, rounded, filled div; see CLAUDE.md → "Cards".
//
// The design is Skin × State × Size. Prop names stay MUI-flavoured per the
// design-system convention, so:
//   variant → Figma *Skin*  (sunken | outline | shadow | stone)
//   size    → Figma *Size*  (lg = 20 padding / 20 radius, sm = 12·16 / 12)
// Hover and pressed are the design's own State axis and come free with
// `interactive` (`interactive="hover"` takes Hover without Active, for a card
// whose hit target is a control inside it; `"press"` takes Active without Hover); `selected` pins the pressed look for
// a card that stays chosen.
//
// Polymorphic: pass `component` and the whole card becomes the hit target while
// still reading as a surface. For an in-app route pass `component={Link}`, never
// `"a"`: a plain anchor reloads the whole app and throws away the open chat.
export type CardVariant = 'sunken' | 'outline' | 'shadow' | 'stone';
export type CardSize = 'sm' | 'lg';

export interface CardProps extends HTMLAttributes<HTMLElement> {
  variant?: CardVariant;
  size?: CardSize;
  /**
   * Hover + pressed states — use when the whole card is clickable.
   * `'hover'` takes the Hover state only, for a card that lights under the pointer while the real
   * hit target is a control inside it — such a card must not flash a pressed border for a press it
   * does not act on. `'press'` is the mirror — Active only, for a card that acts but draws no hover.
   */
  interactive?: boolean | 'hover' | 'press';
  /** Pins the design's Active state, for a card that stays chosen. */
  selected?: boolean;
  /** Edge-to-edge content (a table, a media header) supplies its own padding. */
  disablePadding?: boolean;
  component?: ElementType;
  children?: ReactNode;
}

// Forwards its ref, like `ScrollArea` does, so a caller can observe the real surface element — a BI
// impression needs an actual box to watch (`lib/use-item-seen-bi`), and a plain function component
// would swallow the ref and leave the observer with nothing, silently reporting no impressions.
export const Card = forwardRef<HTMLElement, CardProps>(function Card({
  variant = 'sunken',
  size = 'lg',
  interactive,
  selected,
  disablePadding,
  component,
  className,
  children,
  ...rest
}, ref) {
  const Component = component ?? 'div';
  const classes = [
    styles.root,
    styles[`skin-${variant}`],
    styles[`size-${size}`],
    disablePadding && styles.disablePadding,
    interactive && styles.interactive,
    interactive === 'hover' && styles.hoverOnly,
    interactive === 'press' && styles.pressOnly,
    selected && styles.selected,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Component ref={ref} className={classes} type={Component === 'button' ? 'button' : undefined} {...rest}>
      {children}
    </Component>
  );
});
